/**
 * tianai-captcha 验证码服务
 * 职责：封装 tianai-captcha 官方前端 SDK 的初始化和调用
 * 官方文档: https://doc.captcha.tianai.cloud/
 *
 * 注意事项：
 * - 验证码配置获取通过 publish 后端代理
 * - 验证码生成和验证接口需要直接调用 shared-service（第三方 SDK 要求）
 *
 * 使用方式：
 * import tianaiCaptcha from './services/captcha';
 *
 * // 在需要验证时调用
 * tianaiCaptcha.verify({
 *   bindEl: '#captcha-box',
 *   onValidSuccess: (token) => { console.log('验证成功', token); },
 *   onValidFail: () => { console.log('验证失败'); }
 * });
 */

import api from './api';

// 认证服务地址（验证码生成/验证需要直接调用 shared-service）
// 这是特殊情况，因为第三方 tianai-captcha SDK 需要直接访问验证接口
const AUTH_BASE_URL = process.env.REACT_APP_AUTH_URL || 'http://localhost:8081';

// tac 文件路径（从 public 目录加载）
const TAC_PATH = '/tac';
const LOAD_SCRIPT_URL = `${TAC_PATH}/load.min.js`;

// SDK 加载状态
let scriptLoadPromise = null;
let isScriptLoaded = false;

function waitForElement(selector, timeoutMs = 2000) {
  const startTime = Date.now();
  return new Promise((resolve, reject) => {
    const tick = () => {
      const el = document.querySelector(selector);
      if (el) {
        resolve(el);
        return;
      }
      if (Date.now() - startTime > timeoutMs) {
        reject(new Error(`验证码容器未找到: ${selector}`));
        return;
      }
      setTimeout(tick, 50);
    };
    tick();
  });
}

/**
 * 加载 tianai-captcha 初始化脚本 (load.min.js)
 * @returns {Promise<void>}
 */
function loadScript() {
  if (isScriptLoaded) {
    return Promise.resolve();
  }
  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    // 检查是否已存在 initTAC
    if (window.initTAC) {
      isScriptLoaded = true;
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = LOAD_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      isScriptLoaded = true;
      resolve();
    };
    script.onerror = () => {
      scriptLoadPromise = null;
      reject(new Error('加载 tianai-captcha initTAC 失败，请检查 ' + LOAD_SCRIPT_URL));
    };
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

/**
 * 获取验证码配置（通过 publish 后端代理）
 * @returns {Promise<{enabled: boolean, type: string, randomTypeEnabled: boolean, availableTypes: string[]}>}
 */
async function getConfig() {
  try {
    // 通过 publish 后端代理获取验证码配置
    const response = await api.get('/api/pubs/v1/proxy/auth/captcha/config');
    return response.data || { enabled: false, type: 'SLIDER', randomTypeEnabled: false, availableTypes: ['SLIDER'] };
  } catch (error) {
    console.error('[tianai-captcha] 获取配置失败:', error);
    return { enabled: false, type: 'SLIDER', randomTypeEnabled: false, availableTypes: ['SLIDER'] };
  }
}

/**
 * 根据配置选择验证码类型
 * @param {object} config - 验证码配置
 * @param {string} defaultType - 默认类型（当配置未指定时使用）
 * @returns {string} 选择的验证码类型
 */
function selectCaptchaType(config, defaultType = 'SLIDER') {
  // 如果启用了随机且有可用类型列表，则随机选择
  if (config?.randomTypeEnabled && config?.availableTypes && config.availableTypes.length > 0) {
    const randomIndex = Math.floor(Math.random() * config.availableTypes.length);
    return config.availableTypes[randomIndex];
  }
  // 否则使用配置中的默认类型或传入的默认类型
  return config?.type || defaultType;
}

/**
 * 验证码验证服务类
 * 封装官方 initTAC 的调用逻辑
 */
class CaptchaService {
  constructor() {
    this.tacInstance = null;
    this.currentConfig = null;
  }

  async getConfig() {
    if (this.currentConfig) return this.currentConfig;
    this.currentConfig = await getConfig();
    return this.currentConfig;
  }

  /**
   * 初始化验证码（需要在首次使用前调用）
   */
  async init() {
    await loadScript();
    this.currentConfig = await this.getConfig();
    // console.log('[tianai-captcha] Config loaded:', this.currentConfig);
    // console.log('[tianai-captcha] window.initTAC:', typeof window.initTAC);
    return this.currentConfig;
  }

  /**
   * 显示验证码（使用官方 initTAC 方式）
   * @param {object} options - 配置选项
   * @returns {Promise<{success: boolean, token?: string, message?: string}>}
   */
  async show(options = {}) {
    const {
      bindEl = '#captcha-box',
      type = null,  // 如果不指定，则根据配置自动选择（支持随机）
      onValidSuccess = null,
      onValidFail = null,
      onReady = null,
      style = null
    } = options;

    return new Promise(async (resolve, reject) => {
      try {
        await this.init();

        if (!this.currentConfig?.enabled) {
          // 验证码未启用，直接返回成功
          resolve({ success: true, message: '验证码功能未启用' });
          return;
        }

        // 选择验证码类型：如果未指定 type，则根据配置选择（支持随机）
        const selectedType = type || selectCaptchaType(this.currentConfig, 'SLIDER');
        // console.log('[tianai-captcha] Selected type:', selectedType);
        // console.log('[tianai-captcha] Bind element:', bindEl);

        await waitForElement(bindEl);

        // 官方 initTAC 配置
        const config = {
          // 生成接口（官方标准，直接调用 shared-service，SDK 要求）
          requestCaptchaDataUrl: `${AUTH_BASE_URL}/api/shrd/v1/captcha/gen?type=${selectedType}`,
          // 验证接口（官方标准，直接调用 shared-service，SDK 要求）
          validCaptchaUrl: `${AUTH_BASE_URL}/api/shrd/v1/captcha/check`,
          // 绑定的 DOM 元素
          bindEl: bindEl,
          // 验证成功回调
          validSuccess: (res, c, tac) => {
            const validToken = res?.data?.validToken ?? res?.data?.token;
            if (onValidSuccess) {
              onValidSuccess(validToken, tac);
            }
            resolve({ success: true, token: validToken });
          },
          // 验证失败回调
          validFail: (res, c, tac) => {
            if (onValidFail) {
              onValidFail(res, tac);
            }
            // 验证失败后刷新验证码
            tac.reloadCaptcha();
            resolve({ success: false, message: res?.message || '验证失败' });
          },
          // 刷新按钮回调
          btnRefreshFun: (el, tac) => {
            tac.reloadCaptcha();
          },
          // 关闭按钮回调
          btnCloseFun: (el, tac) => {
            tac.destroyWindow();
            resolve({ success: false, message: '用户关闭' });
          }
        };

        // 样式配置
        const styleConfig = style || {
          logoUrl: null, // 去除 logo
        };

        // 调用官方 initTAC
        // console.log('[tianai-captcha] Calling initTAC with config:', config);
        this.tacInstance = await window.initTAC(TAC_PATH + '/', config, styleConfig);
        // console.log('[tianai-captcha] tacInstance:', this.tacInstance);

        // 显示验证码
        if (typeof this.tacInstance.init === 'function') {
          this.tacInstance.init();
          // console.log('[tianai-captcha] init() called');
        }

        if (onReady) {
          onReady(this.tacInstance);
        }

      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * 销毁验证码
   */
  destroy() {
    if (this.tacInstance) {
      if (typeof this.tacInstance.destroyWindow === 'function') {
        this.tacInstance.destroyWindow();
      }
      this.tacInstance = null;
    }
  }

  /**
   * 刷新验证码
   */
  refresh() {
    if (this.tacInstance && typeof this.tacInstance.reloadCaptcha === 'function') {
      this.tacInstance.reloadCaptcha();
    }
  }
}

// 创建单例实例
const captchaService = new CaptchaService();

/**
 * 导出的 tianai-captcha 服务
 */
const tianaiCaptcha = {
  // 初始化
  init: () => captchaService.init(),

  // 获取配置
  getConfig: () => captchaService.getConfig(),

  // 显示验证码（快捷方法）
  show: (options) => captchaService.show(options),

  // 销毁验证码
  destroy: () => captchaService.destroy(),

  // 刷新验证码
  refresh: () => captchaService.refresh(),

  // 创建新的验证码实例（用于多场景）
  create: () => new CaptchaService(),
};

export default tianaiCaptcha;
